import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { teamApi, InviteAcceptResult, InviteVerifyResult } from '@/api/teamApi';
import { CheckCircle2, XCircle, Users, LogIn, UserPlus, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

type Phase =
  | 'verifying'       // GET verify 호출 중
  | 'confirmed'       // verify OK → 수락 버튼 대기
  | 'accepting'       // POST accept 호출 중
  | 'success'         // 수락 완료
  | 'signup-required' // 초대 이메일로 가입된 계정 없음
  | 'already-member'  // 409 이미 멤버
  | 'expired'         // 링크 만료·사용됨 (410/404)
  | 'error';          // 기타 오류

const roleLabel = (role?: string) => {
  if (role === 'ADMIN') return '관리자';
  if (role === 'OWNER') return '팀장';
  return '멤버';
};

export default function TeamInviteAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user, setMyTeams, setActiveTeamId } = useAuth();

  const [phase, setPhase] = useState<Phase>('verifying');
  const [verifyData, setVerifyData] = useState<InviteVerifyResult | null>(null);
  const [acceptResult, setAcceptResult] = useState<InviteAcceptResult | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  // 마운트 시 항상 verify → 팀 정보 표시 후 수락 버튼 제공
  useEffect(() => {
    if (!token) {
      setErrorMessage('유효하지 않은 초대 링크입니다.');
      setPhase('error');
      return;
    }

    teamApi.verifyInvite(token)
      .then(res => {
        const data = res.data;
        if (!data?.valid) {
          setErrorMessage(data?.message || '초대 링크가 만료되었거나 유효하지 않습니다.');
          setPhase('expired');
          return;
        }
        setVerifyData(data);
        setPhase('confirmed');
      })
      .catch(err => {
        const status = err.response?.status;
        const msg = err.response?.data?.message;
        if (status === 404 || status === 410) {
          setErrorMessage(msg || '초대 링크가 만료되었거나 이미 사용된 링크입니다.');
          setPhase('expired');
        } else {
          setErrorMessage(msg || '초대 정보를 불러올 수 없습니다.');
          setPhase('error');
        }
      });
  }, [token]);

  // JWT 있으면 자동 포함, 없으면 B/E가 invitedEmail → userId 조회
  const doAccept = async () => {
    if (!token) return;
    setPhase('accepting');
    try {
      const res = await teamApi.acceptInvite(token);
      setAcceptResult(res.data);
      setPhase('success');
      // 로그인 상태일 때만 팀 목록 갱신
      if (isAuthenticated) {
        try {
          const teamsRes = await teamApi.getMyTeams();
          setMyTeams(teamsRes.data || []);
          if (res.data?.teamId) setActiveTeamId(res.data.teamId);
        } catch { /* 무시 */ }
      }
    } catch (err: any) {
      const status = err.response?.status;
      const msg = err.response?.data?.message;
      const code = err.response?.data?.error || err.response?.data?.code || err.response?.data?.errorCode;
      if (status === 400 && code === 'SIGNUP_REQUIRED') {
        setPhase('signup-required');
      } else if (status === 409) {
        setErrorMessage(msg || '이미 해당 팀의 멤버입니다.');
        setPhase('already-member');
      } else if (status === 410 || status === 404) {
        setErrorMessage(msg || '초대 링크가 만료되었거나 이미 사용된 링크입니다.');
        setPhase('expired');
      } else {
        setErrorMessage(msg || '초대 수락 중 오류가 발생했습니다.');
        setPhase('error');
      }
    }
  };

  // ── 로딩 화면 ──
  if (phase === 'verifying' || phase === 'accepting') {
    const msg = phase === 'accepting' ? '초대를 수락하는 중입니다...' : '초대 정보를 확인하는 중입니다...';
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Spinner size="3xl" className="text-primary mx-auto mb-3" />
          <p className="text-gray-600">{msg}</p>
        </div>
      </div>
    );
  }

  // ── 초대 확인 + 수락 버튼 ──
  if (phase === 'confirmed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-md w-full mx-4 text-center">
          <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-7 h-7 text-primary" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">팀 초대를 받으셨습니다</h1>
          {verifyData && (
            <>
              <p className="text-gray-600 mb-1">
                <span className="font-semibold text-gray-900">{verifyData.teamName}</span> 팀에서 초대했습니다.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                역할: <span className="font-medium">{roleLabel(verifyData.role)}</span>
              </p>
            </>
          )}
          {isAuthenticated && user && user.subscriptionPlan !== 'FREE' && (
            <div className="flex items-start gap-2 text-left text-xs text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded-lg mb-4">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                현재 개인 유료 플랜을 이용 중입니다. 팀에 가입해도 개인 구독이 자동으로 해지되지 않아 결제가 중복될 수 있습니다.
                가입 후 프로필 &gt; 요금제 메뉴에서 개인 구독 상태를 확인해 주세요.
              </span>
            </div>
          )}
          <Button className="w-full" onClick={doAccept}>
            <Users className="w-4 h-4 mr-2" />
            초대 수락하기
          </Button>
        </div>
      </div>
    );
  }

  // ── 회원가입 필요 ──
  if (phase === 'signup-required') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-md w-full mx-4 text-center">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-7 h-7 text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">계정이 필요합니다</h1>
          <p className="text-gray-600 mb-2">
            초대받은 이메일로 가입된 계정이 없습니다.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            회원가입 후 초대 링크를 다시 클릭하시면 팀에 자동으로 합류됩니다.
          </p>
          <Button className="w-full" onClick={() => navigate('/signup')}>
            <UserPlus className="w-4 h-4 mr-2" />
            회원가입하기
          </Button>
        </div>
      </div>
    );
  }

  // ── 수락 완료 ──
  if (phase === 'success' && acceptResult) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-md w-full mx-4 text-center">
          <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">팀 합류 완료!</h1>
          <p className="text-gray-600 mb-1">
            <span className="font-semibold text-gray-900">{acceptResult.teamName}</span> 팀에 합류했습니다.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            역할: <span className="font-medium">{roleLabel(acceptResult.role)}</span>
          </p>
          {isAuthenticated ? (
            <Button className="w-full" onClick={() => navigate('/dashboard/profile/edit', { replace: true })}>
              팀 관리 페이지로 이동
            </Button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">로그인하면 팀을 바로 이용할 수 있습니다.</p>
              <Button className="w-full" onClick={() => {
                sessionStorage.setItem('redirectAfterLogin', '/dashboard/profile/edit');
                navigate('/login');
              }}>
                <LogIn className="w-4 h-4 mr-2" />
                로그인하여 팀 관리하기
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── 이미 멤버 ──
  if (phase === 'already-member') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-md w-full mx-4 text-center">
          <CheckCircle2 className="w-14 h-14 text-blue-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">이미 팀 멤버입니다</h1>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          {isAuthenticated ? (
            <Button className="w-full" onClick={() => navigate('/dashboard/profile/edit', { replace: true })}>
              팀 관리 페이지로 이동
            </Button>
          ) : (
            <Button className="w-full" onClick={() => navigate('/login')}>
              <LogIn className="w-4 h-4 mr-2" />
              로그인하기
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ── 만료 / 오류 ──
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white rounded-xl shadow-md p-8 max-w-md w-full mx-4 text-center">
        <XCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          {phase === 'expired' ? '초대 링크 만료' : '초대 수락 실패'}
        </h1>
        <p className="text-gray-600 mb-6">{errorMessage}</p>
        <Button variant="outline" className="w-full" onClick={() => navigate('/', { replace: true })}>
          홈으로 이동
        </Button>
      </div>
    </div>
  );
}
